import axios from 'axios';
import { refresh as refreshSession } from '../../api/auth';
import { attachCache } from '../../utils/cache/axiosCache';
import { sessionHeaders, isAccountDeactivatedDetail, forceLogout } from '../../utils/session';

const api = axios.create({
  baseURL:
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_TEAMLIO_API_URL ||
    'http://localhost:8000',
  timeout: 30000,
});

api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    if (config.headers) {
      // Single-session identity. Without these headers the backend cannot
      // tell this request apart from any other device, so /api/auth/sessions
      // computed every row's is_current as false — including the caller's
      // own live session, which then showed a "Sign out" button that revoked
      // the user's own slot and signed them out of the app they were using.
      Object.assign(config.headers, await sessionHeaders());
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The account itself was deactivated (TeamLeo's get_current_user refuses
    // it on every route). No refresh can save this session — its
    // /api/auth/refresh refuses it with the same detail — so skip the retry
    // and sign out with the reason for the login screen to show.
    if (error.response?.status === 401 && isAccountDeactivatedDetail(error.response?.data?.detail)) {
      forceLogout(error.response.data.detail);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const stored = localStorage.getItem('refreshToken');
        if (stored) {
          const result = await refreshSession(stored);
          if (result?.access_token) {
            localStorage.setItem('accessToken', result.access_token);
            if (result.refresh_token) {
              localStorage.setItem('refreshToken', result.refresh_token);
            }
            originalRequest.headers.Authorization = `Bearer ${result.access_token}`;
            return api(originalRequest);
          }
        }
      } catch {
        // fall through to logout
      }

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      const isFile = typeof window !== 'undefined' && window.location.protocol === 'file:';
      window.location.href = isFile ? '#/login' : '/login';
    }

    return Promise.reject(error);
  }
);

// Read-through disk cache. Registered last so the 401-refresh interceptor above
// gets first refusal — this layer must only see failures that cannot be retried.
attachCache(api, 'hrms');

export default api;
